import { styled } from "@pigment-css/react";
import type { BarChart, ChartSection as ChartSectionSchema } from "@acto/genui-components-schema";
import { MetaLabel } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { type BarEmphasis, TrendBarChart } from "@acto/taskpane/field-sales-2/components/primitives/TrendBarChart";
import { chartPeriods } from "./chartPeriods";
import { SentimentColor } from "./SentimentColor";
import { useChartDisplay } from "./useChartDisplay";

const TOOLTIP_LABEL_KEY = "__tooltipLabel";

export const ChartViz = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
}));

export const ChartCaption = styled(MetaLabel)(({ theme }) => ({
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

interface PeriodBarChartProps {
  chart: BarChart;
  granularity: ChartSectionSchema["granularity"];
  barEmphasis?: readonly BarEmphasis[];
}

export function PeriodBarChart({ chart, granularity, barEmphasis }: PeriodBarChartProps) {
  const { locale, currency, leadSeries, comparisonSeries } = useChartDisplay(chart);

  const starts = chart.data.map((row) => String(row[chart.x.field] ?? ""));
  const periods = chartPeriods(starts, granularity, locale);
  const rows = chart.data.map((row, index) => ({ ...row, [TOOLTIP_LABEL_KEY]: periods[index].tooltipLabel }));

  return (
    <TrendBarChart
      data={rows}
      xField={chart.x.field}
      leadSeries={leadSeries}
      comparisonSeries={comparisonSeries}
      periodStarts={starts}
      axisLabels={periods.map((period) => period.axisLabel)}
      barEmphasis={barEmphasis}
      currency={currency}
      locale={locale}
      tooltipLabelField={TOOLTIP_LABEL_KEY}
    />
  );
}

export function ChartSection({ section }: { section: ChartSectionSchema }) {
  return (
    <ChartViz>
      {section.caption && (
        <ChartCaption>
          {section.caption.map((fragment, index) => (
            <SentimentColor key={index} sentiment={fragment.sentiment ?? "neutral"}>
              {fragment.text}
            </SentimentColor>
          ))}
        </ChartCaption>
      )}
      <PeriodBarChart chart={section.chart} granularity={section.granularity} />
    </ChartViz>
  );
}
